<template>
  <el-drawer
    :title="$t('member.memberLevelProgress.title')"
    :visible="true"
    append-to-body
    direction="rtl"
    size="460px"
    class="member-level-progress-drawer"
    @close="$emit('close')"
  >
    <div v-loading="loading" class="drawer-content">
      <!-- 頂部資訊列 -->
      <BaseElForm label-position="left" label-width="100px">
        <BaseElFormItem :label="$t('member.memberLevelProgress.currentLevel')" :show-message="false">
          {{ currentLevelName }}
        </BaseElFormItem>
        <BaseElFormItem :label="$t('member.memberLevelProgress.levelExpiryDate')" :show-message="false">
          {{ levelExpiredAtDisplay }}
        </BaseElFormItem>
      </BaseElForm>

      <el-divider />

      <!-- 分頁 tabs -->
      <el-tabs v-model="activeTab">
        <!-- 升等條件 tab -->
        <el-tab-pane :label="$t('member.memberLevelProgress.upgradeCondition')" name="upgrade">
          <div v-if="upgradeProgressGroups.length" class="condition-groups">
            <div
              v-for="group in upgradeProgressGroups"
              :key="group.targetLevel"
              class="condition-group"
            >
              <div class="group-header">
                <span class="group-title">{{ group.targetLevelName }}</span>
                <span class="group-logic">
                  <template v-if="isOrLogic(group)">{{ $t('member.memberLevelProgress.meetAnyToUpgrade') }}</template>
                  <template v-else>
                    <span>{{ $t('member.memberLevelProgress.meetAllToUpgrade') }}</span>
                    <span class="ml-2">{{ $t('member.memberLevelProgress.conditionMet') }}: {{ satisfiedCount(group) }}/{{ group.conditionProgress.length }}</span>
                  </template>
                </span>
              </div>
              <ConditionCard
                v-for="cond in group.conditionProgress"
                :key="cond.conditionId"
                :condition="cond"
                :current-level-name="currentLevelName"
                :target-level-name="group.targetLevelName"
                :is-renewal="false"
                :expired-at="progressData ? progressData.levelExpiredAt : null"
                :custom-text="cond.customText"
              />
            </div>
          </div>
          <EmptyBlock v-else />
        </el-tab-pane>

        <!-- 續等條件 tab -->
        <el-tab-pane :label="$t('member.memberLevelProgress.renewalCondition')" name="renewal">
          <div v-if="renewalProgressGroup" class="condition-groups">
            <div class="condition-group">
              <div class="group-header">
                <span class="group-title">{{ renewalProgressGroup.targetLevelName }}</span>
                <span class="group-logic">
                  <template v-if="isOrLogic(renewalProgressGroup)">{{ $t('member.memberLevelProgress.meetAnyToRenew') }}</template>
                  <template v-else>
                    <span>{{ $t('member.memberLevelProgress.meetAllToRenew') }}</span>
                    <span class="ml-2">{{ $t('member.memberLevelProgress.conditionMet') }}: {{ satisfiedCount(renewalProgressGroup) }}/{{ renewalProgressGroup.conditionProgress.length }}</span>
                  </template>
                </span>
              </div>
              <ConditionCard
                v-for="cond in renewalProgressGroup.conditionProgress"
                :key="cond.conditionId"
                :condition="cond"
                :current-level-name="currentLevelName"
                :target-level-name="renewalProgressGroup.targetLevelName"
                :is-renewal="true"
                :expired-at="progressData ? progressData.levelExpiredAt : null"
                :custom-text="cond.customText"
              />
            </div>
          </div>
          <EmptyBlock v-else />
        </el-tab-pane>
      </el-tabs>
    </div>
  </el-drawer>
</template>

<script>
import { computed, defineComponent, onMounted, ref } from 'vue'
import { t } from '@/plugins/i18n/i18n'
import { GetMemberLevelProgress, GetMemberLevelConfig } from '@/api/memberLevel'
import { GetMemberLevelRule } from '@/api/memberLevelBenefitSettings'
import { useShop } from '@/use/shop'
import { useFetch } from '@/use/fetch'
import { formatDate } from '@/utils/date'
import { get, keyBy } from 'lodash'
import ConditionCard from './components/ConditionCard.vue'
import EmptyBlock from '@/components/EmptyBlock.vue'

export default defineComponent({
  name: 'MemberLevelProgressDrawer',
  components: { ConditionCard, EmptyBlock },
  props: {
    memberId: { type: String, required: true },
  },
  setup (props) {
    const { shopId } = useShop()
    const { simpleFetch } = useFetch()
    const loading = ref(false)
    const progressData = ref(null)
    const levelConfigMap = ref({})
    const levelRuleData = ref(null)
    const activeTab = ref('upgrade')

    const buildProgressSettingKey = (currentLevel, targetLevel, conditionId) => {
      return `${currentLevel}-${targetLevel}-${conditionId}`
    }

    const buildProgressGroupKey = (currentLevel, targetLevel) => {
      return `${currentLevel}-${targetLevel}`
    }

    const getLevelName = (level) => {
      return get(levelConfigMap.value, [level, 'name'], t('member.memberLevelProgress.levelFallback', [level]))
    }

    const currentLevelName = computed(() => {
      if (!progressData.value) return ''
      return getLevelName(progressData.value.currentLevel)
    })

    const levelExpiredAtDisplay = computed(() => {
      const expiredAt = get(progressData.value, 'levelExpiredAt')
      if (!expiredAt) return t('member.memberLevelProgress.permanent')
      return formatDate(expiredAt, 'YYYY/MM/DD')
    })

    const upgradeConditionLogicMap = computed(() => {
      const rules = get(levelRuleData.value, 'upgradeRules', [])
      const map = {}
      for (const rule of rules) {
        map[`${rule.fromLevel}-${rule.targetLevel}`] = rule.conditionLogic
      }
      return map
    })

    const renewalConditionLogicMap = computed(() => {
      const rules = get(levelRuleData.value, 'renewalRules', [])
      const map = {}
      for (const rule of rules) {
        map[`${rule.fromLevel}-${rule.targetLevel}`] = rule.conditionLogic
      }
      return map
    })

    const adminVisibleUpgradeConditionKeys = computed(() => {
      const items = get(levelRuleData.value, 'progressBarSettings.upgradeProgress', [])
      return new Set(
        items
          .filter((item) => (item.displayLocations || []).includes('adminMemberManage'))
          .map((item) => buildProgressSettingKey(item.currentLevel, item.targetLevel, item.conditionId)),
      )
    })

    const configuredUpgradeGroupKeys = computed(() => {
      const items = get(levelRuleData.value, 'progressBarSettings.upgradeProgress', [])
      return new Set(
        items.map((item) => buildProgressGroupKey(item.currentLevel, item.targetLevel)),
      )
    })

    const adminVisibleRenewalConditionKeys = computed(() => {
      const items = get(levelRuleData.value, 'progressBarSettings.renewalProgress', [])
      return new Set(
        items
          .filter((item) => (item.displayLocations || []).includes('adminMemberManage'))
          .map((item) => buildProgressSettingKey(item.currentLevel, item.targetLevel, item.conditionId)),
      )
    })

    const configuredRenewalGroupKeys = computed(() => {
      const items = get(levelRuleData.value, 'progressBarSettings.renewalProgress', [])
      return new Set(
        items.map((item) => buildProgressGroupKey(item.currentLevel, item.targetLevel)),
      )
    })

    const upgradeCustomTextMap = computed(() => {
      const items = get(levelRuleData.value, 'progressBarSettings.upgradeProgress', [])
      return items.reduce((map, item) => {
        map[buildProgressSettingKey(item.currentLevel, item.targetLevel, item.conditionId)] = item.customText ?? null
        return map
      }, {})
    })

    const renewalCustomTextMap = computed(() => {
      const items = get(levelRuleData.value, 'progressBarSettings.renewalProgress', [])
      return items.reduce((map, item) => {
        map[buildProgressSettingKey(item.currentLevel, item.targetLevel, item.conditionId)] = item.customText ?? null
        return map
      }, {})
    })

    const sortConditionProgress = (conditions, conditionLogic) => {
      if (conditionLogic === 'OR') {
        return [...conditions].sort((a, b) => b.percentage - a.percentage)
      }
      return [...conditions].sort((a, b) => {
        const aSatisfied = a.satisfied ? 1 : 0
        const bSatisfied = b.satisfied ? 1 : 0
        if (aSatisfied !== bSatisfied) return aSatisfied - bSatisfied
        return b.percentage - a.percentage
      })
    }

    const upgradeProgressGroups = computed(() => {
      const groups = get(progressData.value, 'upgradeProgress', [])
      const currentLevel = get(progressData.value, 'currentLevel', 0)
      return groups
        .map((group) => {
          const conditionLogic = upgradeConditionLogicMap.value[`${currentLevel}-${group.targetLevel}`] || 'AND'
          const groupKey = buildProgressGroupKey(currentLevel, group.targetLevel)
          const hasConfiguredGroup = configuredUpgradeGroupKeys.value.has(groupKey)
          const filteredConditions = group.conditionProgress.filter(
            (cond) => {
              if (!hasConfiguredGroup) return true
              return adminVisibleUpgradeConditionKeys.value.has(
                buildProgressSettingKey(currentLevel, group.targetLevel, cond.conditionId),
              )
            },
          )
          return {
            ...group,
            targetLevelName: getLevelName(group.targetLevel),
            conditionLogic,
            conditionProgress: sortConditionProgress(
              filteredConditions.map((cond) => ({
                ...cond,
                customText: cond.customText ?? upgradeCustomTextMap.value[
                  buildProgressSettingKey(currentLevel, group.targetLevel, cond.conditionId)
                ] ?? null,
              })),
              conditionLogic,
            ),
          }
        })
        .filter((group) => group.conditionProgress.length > 0)
        .sort((a, b) => a.targetLevel - b.targetLevel)
    })

    const renewalProgressGroup = computed(() => {
      const group = get(progressData.value, 'renewalProgress')
      if (!group) return null
      const currentLevel = get(progressData.value, 'currentLevel', 0)
      const groupKey = buildProgressGroupKey(currentLevel, group.targetLevel)
      const hasConfiguredGroup = configuredRenewalGroupKeys.value.has(groupKey)
      const filteredConditions = group.conditionProgress.filter(
        (cond) => {
          if (!hasConfiguredGroup) return true
          return adminVisibleRenewalConditionKeys.value.has(
            buildProgressSettingKey(currentLevel, group.targetLevel, cond.conditionId),
          )
        },
      )
      if (!filteredConditions.length) return null
      const conditionLogic = renewalConditionLogicMap.value[`${currentLevel}-${group.targetLevel}`] || 'AND'
      return {
        ...group,
        conditionProgress: sortConditionProgress(
          filteredConditions.map((cond) => ({
            ...cond,
            customText: cond.customText ?? renewalCustomTextMap.value[
              buildProgressSettingKey(currentLevel, group.targetLevel, cond.conditionId)
            ] ?? null,
          })),
          conditionLogic,
        ),
        targetLevelName: getLevelName(group.targetLevel),
        conditionLogic,
      }
    })

    const isOrLogic = (group) => {
      return get(group, 'conditionLogic') === 'OR'
    }

    const satisfiedCount = (group) => {
      return get(group, 'conditionProgress', []).filter((c) => c.satisfied).length
    }

    onMounted(async () => {
      loading.value = true
      await Promise.all([
        simpleFetch(
          GetMemberLevelProgress,
          { shopId: shopId.value, memberId: props.memberId },
          (res) => { progressData.value = res },
        ),
        simpleFetch(
          GetMemberLevelConfig,
          { shopId: shopId.value },
          (res) => {
            levelConfigMap.value = keyBy(Array.isArray(res) ? res : [], 'level')
          },
        ),
        simpleFetch(
          GetMemberLevelRule,
          { shopId: shopId.value },
          (res) => { levelRuleData.value = res },
        ),
      ])
      loading.value = false
    })

    return {
      loading,
      progressData,
      activeTab,
      currentLevelName,
      levelExpiredAtDisplay,
      upgradeProgressGroups,
      renewalProgressGroup,
      isOrLogic,
      satisfiedCount,
    }
  },
})
</script>

<style scoped lang="postcss">
.drawer-content {
  @apply flex flex-col h-full px-6;
}

.condition-groups {
  @apply flex flex-col;
}

.condition-group {
  @apply mb-6 px-2;
}

.group-header {
  @apply flex justify-between items-center mb-3;
}

.group-title {
  @apply text-base font-semibold text-gray-100;
}

.group-logic {
  @apply text-sub text-gray-80 flex items-center;
}

::v-deep .el-form-item {
  @apply mb-0;
}

::v-deep .el-tabs__content {
  @apply overflow-y-auto;
}
</style>

<style lang="postcss">
.member-level-progress-drawer .el-tabs__header {
  @apply mb-4;
  background-color: transparent !important;
  padding: 0 !important;
  border-radius: 0 !important;
}

.member-level-progress-drawer .el-tabs__nav {
  border: none !important;
}

.member-level-progress-drawer .el-tabs__item {
  @apply text-normal text-gray-80 px-0 mr-6 pb-2;
  background-color: transparent !important;
  border: none !important;
  border-radius: 0 !important;
  height: auto !important;
}

.member-level-progress-drawer .el-tabs__item.is-active {
  @apply text-primary-100 font-medium;
  border-bottom: 2px solid var(--primary-100) !important;
}

.member-level-progress-drawer .el-tabs__active-bar {
  display: none;
}

.member-level-progress-drawer .el-tabs__nav-wrap::after {
  @apply bg-gray-30;
}
</style>
